import { ID, Rule, Strategy, TimeString } from '../../types/models'
import { Appliance, ApplianceConfigRelay, DutyCycleMethod } from './Appliance'
import { RelayData } from '../../types/charts'
import { PortConfig, PortConfigOptimizerControl, PortType, UncommittedConfig } from '../../types/ports'
import { RelayBarWidget } from '../widgets/RelayBarWidget'
import { getWidget } from '../widgets'
import { Widget } from '../widgets/Widget'
import { Box, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { StrategySelector } from '../../components/StrategySelector'
import { OptimizerControlPortControl } from '../ports/OptimizerControlPortControl'
import { RuleEditFixedCalendar } from '../../components/RuleEditFixedCalendar'
import { SystemControl } from '../SystemControl'
import { RelayPortControl } from '../ports/RelayPortControl'
import { DeviceControl } from '../DeviceControl'
import { TextToggle } from '../../components/atoms/TextToggle'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { Dropdown } from '../../components/atoms/Dropdown'
import { StrategySliders } from '../../components/StrategySliders'
import { PhaseSelector } from '../../components/atoms/PhaseSelector'
import { Text } from '../../components/atoms/Text'
import { Safety } from '../Safety'
import { strategyForPlanByPrice, strategyPortConfig } from './strategy'

export class RelayAppliance<ConfigType extends ApplianceConfigRelay = ApplianceConfigRelay> extends Appliance<{ relays: RelayData<TimeString>[] }, ConfigType> {

  getDefaultPortType(): PortType {
    return 'RELAY'
  }

  getDefaultWidgets(): Widget[] {
    return [
      getWidget({
        name: '',
        type: 'RELAY_BAR',
        appliances: [this],
        config: {}
      }, this.control, this.app) as RelayBarWidget,
    ]
  }

  /**
   * Top section of the configurator.
   */
  drawSwitches(): React.ReactNode {
    return <></>
  }

  /**
   * Middle section of the configurator.
   */
  drawSliders(): React.ReactNode {
    return <></>
  }

  /**
   * Bottom section of the configurator.
   */
  drawSelectors(appliances: Appliance[]): React.ReactNode {
    return <></>
  }

  /**
   * Find the optimizer control control.
   */
  getOptimizerControl(): OptimizerControlPortControl {
    // Hmm, this is slightly questionable. Can we quarantee that in future it will be second control?
    return this.getControl(1)
  }

  /**
   * Find the device control of the optimizer control control.
   */
  getDeviceControl(): DeviceControl {
    return this.getOptimizerControl().deviceControl
  }

  /**
   * Toggle for deciding between simple and advanced version of configurator.
   */
  drawAdvancedToggle(): React.ReactNode {
    const advanced = !!this.getConfig('advanced')
    return <Box sx={{ textAlign: 'right' }}>
      <TextToggle
        sx={{ color: 'toggleControl.main' }}
        state={advanced}
        offText={this.app.t('Simple')}
        onText={this.app.t('Advanced')}
        onClick={() => this.setConfig('advanced', !advanced)}
      />
    </Box>
  }

  drawSimple(): React.ReactNode {
    return <></>
  }

  drawAdvanced(appliances: Appliance[]): React.ReactNode {
    // TODO: This is very messy. Needs refactoring to smaller logical pieces.
    const optimizerCtrl = this.getOptimizerControl()
    const planByPrice = !!optimizerCtrl.getConfig('plan_by_price')
    const method: DutyCycleMethod = this.getConfig('opt_by_method')
    const options: Record<DutyCycleMethod, string> = {
      FIXED: this.app.t('Option-Fixed'),
      WEATHER: this.app.t('Option-Weather'),
      CLOCK: this.app.t('Option-Clock'),
    }

    return <Box sx={{ mb: 5 }}>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch data-cy="switch-Plan by Price" onClick={() => this.setPlanByPrice(!planByPrice)} checked={planByPrice} sx={{ ml: '1rem' }}/>
        <Box sx={{ pt: 1, ml: 2 }}><Trans>Plan by price</Trans></Box>
      </Box>
      {
        planByPrice &&
        <Dropdown
          sx={{ ml: 2 }}
          oneLine
          label={this.app.t('Duty cycle')}
          items={options}
          selected={this.getConfig('opt_by_method')}
          onSelect={(v: string) => this.setConfig('opt_by_method', v)}
        />
      }

      <Box sx={{ ml: 2, mt: 2 }}>
        {
         !method && <>
          {this.drawAdvancedSolarStuff()}
          {this.drawAdvancedWeather()}
          {this.drawAdvancedExtras()}
         </>
        }
        {
         method === 'WEATHER' && <>
          {planByPrice && <Box sx={{ mb: 2 }}>{optimizerCtrl.configBottomLeft()}</Box>}
          {this.drawAdvancedWeather()}
          {this.drawAdvancedSolarStuff()}
          {this.drawSelectors(appliances)}
          {this.drawAdvancedExtras()}
         </>
        }
        {
         method === 'FIXED' && <>
          {planByPrice && this.drawAdvancedFixed()}
          {this.drawAdvancedSolarStuff()}
          {this.drawAdvancedWeather()}
          {this.drawAdvancedExtras()}
         </>
        }
        {
         method === 'CLOCK' && <>
          <Trans>Not yet implemented</Trans>
         </>
        }
      </Box>
    </Box>
  }

  drawAdvancedWeather(): React.ReactNode {
    // TODO: c/kWh should be localized properly.

    return <Box>
      <StrategySliders
        noBorder

        name1="heat_preservation"
        slider1={['4h', '24h']}
        values1={[4, 24]}
        value1={this.getConfig('heat_preservation')}
        title1={this.app.t('Time window of optimization?')}

        onChange={(k, v) => this.setConfig(k, v)}
      />
    </Box>
  }

  drawAdvancedSolarStuff(): React.ReactNode {
    const ctrl = this.getOptimizerControl()

    return <Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch
          data-cy="switch-Solar Self Consumption"
          onClick={() => {
            if (ctrl.getConfig('pv_priority')) {
              this.setConfig('pv_priority', 0)
              ctrl.setConfig('pv_priority', 0)
            } else {
              this.setConfig('pv_priority', 8)
              ctrl.setConfig('pv_priority', 8)
            }
          }}
          checked={ctrl.getConfig('pv_priority') as number > 0}
          sx={{ ml: '1rem' }}
        />
        <Box sx={{ pt: 1 }}><Trans>Appliance participates in solar self-consumption</Trans></Box>
      </Box>

      {ctrl.getConfig('pv_priority') as number > 0 && <Box sx={{ mb: 7 }}><StrategySliders
        noBorder

        name1="pv_priority"
        slider1={[this.app.t('Turns on before others'), this.app.t('Turns on after others')]}
        values1={[1, 15]}
        value1={ctrl.getConfig('pv_priority')}
        title1=""

        onChange={(_, v) => {
          this.setConfig('pv_priority', v)
          ctrl.setConfig('pv_priority', v)
        }}
      /></Box>}

      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch
          data-cy="switch-Fuse Protection"
          onClick={() => ctrl.getConfig('fuse_priority') ? ctrl.setConfig('fuse_priority', 0) : ctrl.setConfig('fuse_priority', 8)}
          checked={ctrl.getConfig('fuse_priority') as number > 0}
          sx={{ ml: '1rem' }}
        />
        <Box sx={{ pt: 1 }}><Trans>Appliance participates in fuse protection / power limit</Trans></Box>
      </Box>

      {ctrl.getConfig('fuse_priority') as number > 0 && <Box sx={{ mb: 7 }}><StrategySliders
        noBorder

        name1="fuse_priority"
        slider1={[this.app.t('Turns off before others'), this.app.t('Turns off after others')]}
        values1={[1, 15]}
        value1={ctrl.getConfig('fuse_priority')}
        title1=""

        onChange={(k, v) => ctrl.setConfig(k as keyof PortConfigOptimizerControl, v)}
      /></Box>}

      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch
          data-cy="switch-Fixed Price"
          onClick={() => ctrl.setConfig('use_fixed_price', !ctrl.getConfig('use_fixed_price'))}
          checked={!!ctrl.getConfig('use_fixed_price')}
          sx={{ ml: '1rem' }}
        />
        <Box sx={{ pt: 1 }}><Trans>Turn appliance on when price is below</Trans></Box>
        <Text
          data-cy="text-Fixed buy price threshold"
          margin="normal"
          sx={{ width: '15%', mt: -1, mx: 2 }}
          size="small"
          value={ctrl.getConfig('fixed_buy_price_threshold') || ''}
          type="number"
          min={-128}
          max={127}
          onChange={(e) => ctrl.setConfig('fixed_buy_price_threshold', e.target.value)}
          onBlur={(e) => ctrl.setConfig('fixed_buy_price_threshold', parseInt(e.target.value) || 0)}
        />
        <Box sx={{ pt: 1 }}><Trans>c/kWh</Trans></Box>
      </Box>
    </Box>
  }

  drawAdvancedExtras(): React.ReactNode {
    const ctrl = this.getOptimizerControl()

    if (!ctrl.getConfig('cop_curve')) {
      ctrl.setConfig('cop_curve', [0, 0, 0, 0])
    }
    if (!ctrl.getConfig('phases')) {
      ctrl.setConfig('phases', [true, true, true])
    }

    const setCopCurve = (index: number, value: string) => {
      const curve: [number | string, number | string, number | string, number | string, number | string] = ctrl.getConfig('cop_curve')
      curve[index] = value
      ctrl.setConfig('cop_curve', curve)
    }

    const finishCopCuve = () => {
      const curve: [number | string, number | string, number | string, number | string, number | string] = ctrl.getConfig('cop_curve')
      ctrl.setConfig('cop_curve', [
        parseFloat((curve[0] + '').replace(',', '.') + ''),
        parseFloat((curve[1] + '').replace(',', '.') + ''),
        parseFloat((curve[2] + '').replace(',', '.') + ''),
        parseFloat((curve[3] + '').replace(',', '.') + '')
      ])
    }

    return <Box sx={{ mt: 3 }}>
      <Typography><Trans>Information about the appliance</Trans></Typography>

      <Box sx={{ display: 'flex', mt: 2, mb: 2 }}>
        <Trans>Consumes current on phases</Trans>
        <PhaseSelector sx={{ pl: 2, mt: -1 }} value={ctrl.getConfig('phases')} onChange={phases => ctrl.setConfig('phases', phases)}/>
      </Box>

      <Box>
        <Trans>Estimated electric power</Trans>
        <Text
            data-cy="text-Estimated electric power"
            margin="normal"
            sx={{ width: '25%', ml: 2, mt: -1 }}
            size="small"
            value={ctrl.getConfig('appliance_power') || ''}
            type="number"
            onChange={(e) => ctrl.setConfig('appliance_power', e.target.value)}
            onBlur={(e) => ctrl.setConfig('appliance_power', parseInt(e.target.value) || 0)}
          />&nbsp;W
      </Box>

      <Box>
        <Trans>COP/efficiency estimate at different temperatures</Trans>
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          <Text
            sx={{ pr: 1 }}
            unit="°C"
            label="-20 °C"
            value={(ctrl.getConfig('cop_curve') as [number, number, number, number])[0]}
            onChange={(e) => setCopCurve(0, e.target.value)}
            onBlur={(e) => finishCopCuve()}
          />
          <Text
            sx={{ pr: 0.5 }}
            unit="°C"
            label="-10 °C"
            value={(ctrl.getConfig('cop_curve') as [number, number, number, number])[1]}
            onChange={(e) => setCopCurve(1, e.target.value)}
            onBlur={(e) => finishCopCuve()}
          />
          <Text
            sx={{ pl: 0.5 }}
            unit="°C"
            label="0 °C"
            value={(ctrl.getConfig('cop_curve') as [number, number, number, number])[2]}
            onChange={(e) => setCopCurve(2, e.target.value)}
            onBlur={(e) => finishCopCuve()}
          />
          <Text
            sx={{ pl: 1 }}
            unit="°C"
            label="+10 °C"
            value={(ctrl.getConfig('cop_curve') as [number, number, number, number])[3]}
            onChange={(e) => setCopCurve(3, e.target.value)}
            onBlur={(e) => finishCopCuve()}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch
          data-cy="switch-Good Sink"
          onClick={() => ctrl.setConfig('good_sink', !ctrl.getConfig('good_sink'))}
          checked={!!ctrl.getConfig('good_sink')}
          sx={{ ml: '1rem' }}
        />
        <Box sx={{ pt: 1 }}><Trans>Good sink for excess energy</Trans></Box>
      </Box>

      <Box sx={{ display: 'flex', mt: 2 }}>
        <CustomSwitch
          data-cy="switch-Can Handle Frequent"
          onClick={() => ctrl.setConfig('rapid_switching_ok', !ctrl.getConfig('rapid_switching_ok'))}
          checked={!!ctrl.getConfig('rapid_switching_ok')}
          sx={{ ml: '1rem' }}
        />
        <Box sx={{ pt: 1 }}><Trans>Can handle frequent switching</Trans></Box>
      </Box>

    </Box>
  }

  drawAdvancedFixed(): React.ReactNode {
    return <Box>
      <StrategySliders
        noBorder
        name1="fixed_percentage"
        slider1={['0%', '100%']}
        values1={[0, 100]}
        value1={this.getConfig('fixed_percentage') || 0}
        title1=""

        onChange={(k, v) => {
          this.setConfig(k, v)
          const ctrl = this.getOptimizerControl()
          ctrl.setConfig('min_duty', v)
          ctrl.setConfig('max_duty', v)
        }}
      />
    </Box>
  }

  drawCalendar(): React.ReactNode {
    const ctrl: RelayPortControl = this.findOneControl('RELAY', 'EVSE')

    const rule: Rule = ctrl.device.rules?.find(r => r.id === ctrl.port.rule_id) || {
      type: 'FIXED_CALENDAR',
      priority: 5,
      params: {
        area: this.app.user?.area?.code || 'FI',
        name: 'is_on',
        off_value: 'false',
        on_value: 'true',
        intervals: [],
        visuals: []
      }
    }

    return <Box>
      <RuleEditFixedCalendar
        app={this.app}
        device={ctrl.device}
        port={ctrl.port}
        rule={rule}
        addRule={(hwId, rule) => SystemControl.addRule(hwId, rule)}
        onUpdate={(args) => SystemControl.onUpdate(args)}
      />
    </Box>
  }

  configTop(appliances: Appliance[]): React.ReactNode {

    const installerLevel = Safety.get('installer-level') as boolean
    const advanced = this.hasAdvanced() && !!this.getConfig('advanced')

    const onSelectStrategy = (strategy: Strategy) => {
      this.setConfig('strategy', strategy)
      const optCtrl = this.getOptimizerControl()
      switch (strategy) {
        case 'MANUAL':
          optCtrl.setConfig('enabled', false, false)
          optCtrl.setConfig('plan_by_price', true, false)
          optCtrl.setConfig('pv_priority', 0)
          break
        case 'AUTO':
          optCtrl.setConfig('enabled', true, false)
          optCtrl.setConfig('plan_by_price', true, false)
          optCtrl.setConfig('pv_priority', optCtrl.getConfigDefaults().pv_priority)
          break
        case 'CALENDAR':
          optCtrl.setConfig('enabled', false, false)
          optCtrl.setConfig('plan_by_price', true, false)
          optCtrl.setConfig('pv_priority', 0)
          break
        case 'PV_ONLY':
          optCtrl.setConfig('enabled', true, false)
          optCtrl.setConfig('plan_by_price', false, false)
          optCtrl.setConfig('pv_priority', optCtrl.getConfigDefaults().pv_priority)
          break
        case 'AVOID_EXPENSIVE':
          optCtrl.setConfig('enabled', true, false)
          optCtrl.setConfig('plan_by_price', true, false)
          optCtrl.setConfig('pv_priority', 0)
          break
      }
      this.applyConfig()
    }

    const simpleControls = <>
      {this.drawSwitches()}
      {this.drawSliders()}
      {this.drawSimple()}
      {this.drawSelectors(appliances)}
    </>

    return (
      <Box>
        <Box>
          <Trans>Strategy</Trans>
          <StrategySelector value={this.config.strategy} onSelect={onSelectStrategy} advanced={this.app.userHas('PRICE_OPTIMIZATION')}/>
        </Box>
        {
          ['PV_ONLY', 'AUTO', 'AVOID_EXPENSIVE'].includes(this.config.strategy || '') && <Box>

            {installerLevel ? (this.hasAdvanced() && this.drawAdvancedToggle()) : <></>}

            {advanced ? (installerLevel ? this.drawAdvanced(appliances) : simpleControls) : simpleControls}
          </Box>
        }
        {
          this.config.strategy === 'CALENDAR' && <Box sx={{ my: 2 }}>{this.drawCalendar()}</Box>
        }
      </Box>
    )
  }

  /**
   * Conversion from appliance configuration to optimizer control port config.
   */
  convert(): Record<string, unknown> {
    const pvPriority = this.getConfig('pv_priority')
    return pvPriority
      ? {
          pv_priority: pvPriority,
        }
      : {}
  }

  /**
   * Optimizer control settings derived from the appliance: convert() plus what
   * the selected strategy implies (`enabled`, `plan_by_price`). The strategy
   * chip is what the user sees, so it must win over whatever the port happens
   * to hold — until 2026-08 the chip only wrote the port when it was clicked,
   * and the advanced "Plan by price" switch wrote the port without moving the
   * chip, so an appliance could show AUTO while the device ran PV_ONLY.
   */
  optimizerConfig(): Record<string, unknown> {
    return Object.assign(this.convert(), strategyPortConfig(this.getConfig('strategy') as Strategy | undefined))
  }

  /**
   * Advanced-panel "Plan by price" switch. Moves the strategy chip along with
   * the port bit so the two cannot disagree on save.
   */
  setPlanByPrice(on: boolean): void {
    const ctrl = this.getOptimizerControl()
    ctrl.setConfig('plan_by_price', on, false)
    this.setConfig('strategy', strategyForPlanByPrice(on, (ctrl.getConfig('pv_priority') as number) || 0))
  }

  /**
   * Build the flash payload for both the relay port and optimizer control port.
   * Uses optimizerConfig() output merged with current config defaults for the optimizer control.
   */
  buildFlashPayload(): UncommittedConfig[] {
    const items: UncommittedConfig[] = []

    // Add relay/EVSE port config
    const relayCtrl = this.getControl(0)
    if (relayCtrl) {
      items.push(DeviceControl.buildPortFlashItem(relayCtrl))
    }

    // Add optimizer control port config if available
    try {
      const optCtrl = this.getOptimizerControl()
      if (optCtrl?.port) {
        const conf = this.optimizerConfig()
        const defaults = optCtrl.getConfigDefaults() as unknown as Record<string, unknown>
        const optConfig: Record<string, unknown> = {}
        for (const key of Object.keys(defaults)) {
          const val = key in conf ? conf[key] : optCtrl.getConfig(key)
          optConfig[key] = DeviceControl.coerceConfigValue(val !== undefined ? val : defaults[key], defaults[key])
        }
        items.push({
          id: optCtrl.port.id as ID,
          port: optCtrl.port.port_number,
          config: optConfig as PortConfig,
        })
      }
    } catch {
      // EVSE or other appliances may not have optimizer control
    }

    return items
  }

  /**
   * If automatic, convert settings to optimizer control.
   */
  applyConfig(): void {
    const ctrl = this.getOptimizerControl()
    const conf = this.optimizerConfig()

    Object.keys(conf).forEach(k => {
      ctrl.setConfig(k as keyof PortConfigOptimizerControl, conf[k])
    })
  }

  /**
   * Copy some settings.
   */
  createHook(): void {
    const ctrl = this.getOptimizerControl()
    ctrl.setConfig('fuse_priority', ctrl.getConfig('pv_priority'))
    const strategy = this.getConfig('strategy') as Strategy
    if (['MANUAL', 'CALENDAR'].includes(strategy)) {
      ctrl.setConfig('enabled', false)
    }
  }
}
